import { HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useUpdatePickupPerson } from 'typescript/hooks/use_bopis_client';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { CHECKOUT_ROUTES } from 'typescript/constants/enums/checkout';
import {
  isOrderFree,
  orderRequiresCardOnFile,
} from 'typescript/utilities/order_data';
import { useLogCompleteOrderAction } from 'typescript/apps/checkout_v3/hooks/use_log_complete_order_action';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import DeliveryPageCTA from 'typescript/apps/checkout_v3/components/delivery/delivery_page_cta';
import PickupOptionsFinder from 'typescript/apps/checkout_v3/components/delivery/bopis/pickup_options_finder';
import PickupPersonInfoForm from 'typescript/apps/checkout_v3/components/delivery/bopis/pickup_person_info_form';
import RecaptchaModal from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal';
import SubscriptionDisclosure from 'typescript/apps/checkout_v3/components/payment/subscription_disclosure';
import { useSubscriptionDisclosure } from 'typescript/apps/checkout_v3/hooks/use_subscription_disclosure';
import { usePaymentContext } from 'typescript/context/payment_context';
import usePickupPersonForm from 'typescript/apps/checkout_v3/components/delivery/bopis/use_pickup_person_form';
import { useIsProcessingAnyRequest } from 'typescript/hooks/use_react_query_utils';
import {
  onCheckoutPageClick,
  onCheckoutPageViewCombined,
} from 'typescript/utilities/tracking';
import {
  CHECKOUT_PAGE_CLICK_EVENTS,
  COMPONENT_NAMES,
} from 'typescript/constants/tracking';

const PickupInStore = ({ className }: HTMLAttributes<HTMLDivElement>) => {
  const history = useHistory();
  const { countryCode } = useLocaleInfo();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { isProcessing } = useIsProcessingAnyRequest();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const { updatePickupPerson } = useUpdatePickupPerson({ orderNumber });
  const { setShowRecaptcha } = useCompleteOrderContext();

  // user may previously have selected a location, preselect it
  const initialReservedLocationId = orderSummaryData?.bopis_fulfillment_selected
    ? orderSummaryData?.pickup_location_info?.location_id
    : '';

  // to render user selections before the API returns
  const [chosenPickupLocationId, setChosenPickupLocationId] = useState(
    initialReservedLocationId,
  );

  const isFreeOrder = isOrderFree(orderSummaryData?.payment_due);
  const requiresCardOnFile = orderRequiresCardOnFile(orderSummaryData);
  const completesOnDeliveryPage = isFreeOrder && !requiresCardOnFile;
  const { consentRequired: subscriptionConsentRequired } =
    useSubscriptionDisclosure();
  const { subscriptionConsentGiven, setSubscriptionConsentError } =
    usePaymentContext();

  const { formMethods } = usePickupPersonForm({ countryCode });
  const {
    control,
    formState: { isValid: isFormValid, isValidating: isFormValidating },
    getValues: getPickupPersonValues,
    setValue: setPickupPersonValue,
    handleSubmit,
  } = formMethods;

  const canContinue = useMemo(() => {
    return (
      !isProcessing &&
      !isFormValidating &&
      isFormValid &&
      Boolean(chosenPickupLocationId) &&
      orderSummaryData?.bopis_fulfillment_selected
    );
  }, [
    isProcessing,
    isFormValidating,
    isFormValid,
    chosenPickupLocationId,
    orderSummaryData?.bopis_fulfillment_selected,
  ]);

  const onSubmit = () => {
    // Don't proceed if form is not valid
    if (!canContinue) {
      return;
    }

    if (
      isFreeOrder &&
      subscriptionConsentRequired &&
      !subscriptionConsentGiven
    ) {
      // Free subscription orders need consent before either completion or card
      // capture, since the payment page contains the recurring billing terms.
      setSubscriptionConsentError(true);
      return;
    }

    const pickupPersonFields = getPickupPersonValues();

    updatePickupPerson(
      {
        pickupPersonInfo: {
          first_name: pickupPersonFields.pickup_first_name,
          last_name: pickupPersonFields.pickup_last_name,
          email: pickupPersonFields.pickup_email,
          phone: pickupPersonFields.pickup_phone,
        },
      },
      {
        // this onSuccess runs AFTER the one defined in the useMutation hook
        // so we'll get query invalidation finished before we navigate to the payment page
        onSuccess: () => {
          if (completesOnDeliveryPage) {
            setShowRecaptcha(true);
          } else {
            history.push(CHECKOUT_ROUTES.PAYMENT);
          }
        },
      },
    );
  };

  const { logCompleteOrderAction } = useLogCompleteOrderAction({
    orderNumber,
    orderSummaryData,
  });

  const handleSubmitWithLogging = (callback) => (event?) => {
    onCheckoutPageClick({
      eventDesc: completesOnDeliveryPage
        ? CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_complete_order
        : CHECKOUT_PAGE_CLICK_EVENTS.delivery_page_continue_to_payment,
      trackingData,
    });

    if (completesOnDeliveryPage) {
      logCompleteOrderAction({ status: 'initiated' });
    }

    return handleSubmit(callback)(event);
  };

  // pre-fill pickup person from order
  useEffect(() => {
    const { pickup_person_info: pickupPersonInfo } = orderSummaryData || {};

    Object.entries({ ...pickupPersonInfo }).forEach(([key, value]) => {
      setPickupPersonValue(`pickup_${key}`, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    });
  }, []);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.PICK_UP_IN_STORE,
      additionalParameters: trackingData,
    });
  }, []);

  return (
    <div className="shop-grid-item-full shop-grid-container">
      <PickupOptionsFinder
        chosenPickupLocationId={chosenPickupLocationId}
        setChosenPickupLocationId={setChosenPickupLocationId}
      />
      <FormProvider {...formMethods}>
        <form
          className={`shop-grid-container ${className}`}
          onSubmit={(e) => e.preventDefault()}
        >
          <RecaptchaModal handleFormSubmit={handleSubmitWithLogging} />
          <PickupPersonInfoForm />
          {isFreeOrder && (
            <SubscriptionDisclosure className="shop-grid-item-full" />
          )}
          <DeliveryPageCTA
            className="shop-grid-item-full"
            control={control}
            // Orders that complete on this page need all form fields to be valid.
            // Orders that continue to payment can leave validation to that step.
            disabled={completesOnDeliveryPage && !canContinue}
            isLoading={isProcessing}
            onClick={() => handleSubmitWithLogging(onSubmit)()}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default PickupInStore;
